import { Icon, type IconName } from "./Icon";
import { Brand } from "./ui";
import { LanguagePicker } from "./LanguagePicker";
import { UrduBetaNotice } from "./UrduBetaNotice";
import type { SupabaseClient } from "@supabase/supabase-js";
import { JoinForm } from "./JoinForm";
import { useI18n } from "../lib/i18n";

/**
 * The signed-out front door.
 *
 * This used to be the generic `Standalone` card — the same 480px box used for
 * "loading" and "not connected". That is the right shape for a transient state
 * and the wrong shape for the one screen a family sees before they decide
 * whether this is for them, which has to answer "what is this?" without a tour.
 *
 * So it answers it: what a day looks like, what is inside, and what the app
 * asks of a parent. The marketing case lives on the main site and is linked
 * from the foot of the page; this stays short and concrete, because the person
 * reading it has usually already decided and is looking for the sign-in button.
 */

const FEATURES: { icon: IconName; key: string }[] = [
  { icon: "sun", key: "day" },
  { icon: "message", key: "words" },
  { icon: "camera", key: "evidence" },
  { icon: "target", key: "mastery" },
  { icon: "image", key: "portfolio" },
  { icon: "map", key: "levels" },
];

const STEPS = ["add", "do", "record"];

export function Welcome({
  signInHref,
  supabase,
  onSignedIn,
}: {
  /** Kept as the fallback when auth is not configured for this deployment. */
  signInHref: string;
  supabase?: SupabaseClient | null;
  /** Hands a fresh session to the app; resolves once the app has taken over. */
  onSignedIn?: () => Promise<void> | void;
}) {
  const { t } = useI18n();

  return (
    <main className="landing">
      <div className="ambient ambient-one" />
      <div className="ambient ambient-two" />
      <div className="ambient ambient-three" />

      <div className="landing-inner">
        <header className="landing-top">
          <Brand />
          <LanguagePicker />
        </header>

        {/* No action here: the suggestions page needs an account. The
            notice still belongs on this screen, because this is where a
            family chooses Urdu in the first place. */}
        <UrduBetaNotice />

        <section className="landing-hero">
          <div className="landing-hero-copy">
            <p className="eyebrow landing-eyebrow">{t("landing.eyebrow")}</p>
            <h1>{t("landing.title")}</h1>
            <p className="landing-lede">{t("landing.lede")}</p>
            <div className="landing-actions">
              {/* Only when there is no Supabase client to register against —
                  a local build with auth switched off. Everyone else joins on
                  this page rather than being sent to the tuition marketplace's
                  sign-in and expected to find their way back. */}
              {!supabase && (
                <a className="primary" href={signInHref}>
                  {t("boot.signIn")} <Icon name="arrow" />
                </a>
              )}
              <a
                className="soft-button"
                href="https://www.alnoortuition.com/homeschooling/guidelines"
              >
                {t("landing.guideLink")}
              </a>
            </div>
            <p className="landing-note">
              <Icon name="shield" size={15} /> {t("landing.accountNote")}
            </p>
          </div>

          {/* The form sits beside the promise, above the fold, on the page
              they are already reading. */}
          {supabase && onSignedIn ? (
            <JoinForm supabase={supabase} onSignedIn={onSignedIn} />
          ) : null}

          {/* A real day rather than a picture of one: the same six-moment shape
              the parent will see on their first morning, so the screen makes a
              promise the app immediately keeps. */}
          <aside className="glass-card landing-day" aria-label={t("landing.dayTitle")}>
            <div className="landing-day-head">
              <span>{t("landing.dayEyebrow")}</span>
              <strong>{t("landing.dayTitle")}</strong>
            </div>
            <ul className="landing-day-list">
              {["one", "two", "three", "four"].map((slot, index) => (
                <li key={slot}>
                  <i aria-hidden="true">{index + 1}</i>
                  <div>
                    <strong>{t(`landing.slot.${slot}`)}</strong>
                    <small>{t(`landing.slot.${slot}.meta`)}</small>
                  </div>
                </li>
              ))}
            </ul>
            <p className="landing-day-foot">{t("landing.dayFoot")}</p>
          </aside>
        </section>

        <section className="landing-features" aria-label={t("landing.insideTitle")}>
          <h2>{t("landing.insideTitle")}</h2>
          <div className="landing-feature-grid">
            {FEATURES.map((feature) => (
              <article key={feature.key} className="glass-card landing-feature">
                <span>
                  <Icon name={feature.icon} />
                </span>
                <h3>{t(`landing.feature.${feature.key}`)}</h3>
                <p>{t(`landing.feature.${feature.key}.body`)}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-steps" aria-label={t("landing.stepsTitle")}>
          <h2>{t("landing.stepsTitle")}</h2>
          <ol className="landing-step-list">
            {STEPS.map((step, index) => (
              <li key={step}>
                <i aria-hidden="true">{index + 1}</i>
                <strong>{t(`landing.step.${step}`)}</strong>
                <p>{t(`landing.step.${step}.body`)}</p>
              </li>
            ))}
          </ol>
        </section>

        <footer className="landing-foot">
          <p>{t("landing.privacy")}</p>
          <nav>
            <a href="https://www.alnoortuition.com/homeschooling">{t("landing.linkOverview")}</a>
            <a href="https://www.alnoortuition.com/homeschooling/benefits">{t("landing.linkBenefits")}</a>
            <a href="https://www.alnoortuition.com/homeschooling/guidelines">{t("landing.linkGuidelines")}</a>
            <a href="https://www.alnoortuition.com/homeschooling/policy">{t("landing.linkPolicy")}</a>
          </nav>
        </footer>
      </div>
    </main>
  );
}
